import { Menu, Transition } from '@headlessui/react';
import React, { Fragment, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { HiTrash, HiX } from 'react-icons/hi';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TagState } from '../../../types/Tag';
import { RootState } from '../../../redux/reducers';
import tagService from '../../../services/community/tagService';
import Community from '../../../types/community';

interface CommunityTagButtonProps {
  children: React.ReactNode;
  community: Community;
  onTagsChanged: () => void;
  className?: string;
}

export default function CommunityTagButton({
  children,
  community,
  onTagsChanged,
  className = '',
}: CommunityTagButtonProps): JSX.Element {
  const [visibleTags, setVisibleTags] = React.useState<TagState[]>([]);
  const [inputValue, setInputValue] = React.useState<string>('');
  const { t } = useTranslation();

  const tags = useSelector((state: RootState) => state.communityTags);
  const colors = ['#D9F3F2', '#FFE1E1', '#FFF0E0', '#D8E6F2', '#F2E4D8', '#F2F1D8'];
  const communityTagIds = community.tags ?? [];

  useEffect((): void => {
    setVisibleTags(tags.filter((tag) => tag.name.toLowerCase().includes(inputValue.toLowerCase())));
  }, [tags]);

  useEffect((): void => {
    if (inputValue) {
      setVisibleTags(tags.filter((tag) => tag.name.toLowerCase().includes(inputValue.toLowerCase())));
    } else {
      setVisibleTags(tags);
    }
  }, [inputValue]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
    e.preventDefault();
  };

  const onTagClick = async (tagId: string): Promise<void> => {
    await tagService.assignTagToCommunity(community.id, tagId);
    onTagsChanged();
    setInputValue('');
  };

  const handleTagDelete = async (id: string): Promise<void> => {
    await tagService.deleteCommunityTag(id);
  };

  const calculateColor = (): string => {
    const colorMap = new Map<string, number>();
    const counts: number[] = [];
    for (let i = 0; i < colors.length; i += 1) {
      colorMap.set(colors[i], i);
      counts.push(0);
    }
    for (let i = 0; i < tags.length; i += 1) {
      const idx = colorMap.get(tags[i].color) ?? -1;
      if (idx !== -1) counts[idx] += 1;
    }
    let leastColor = colors[0];
    let leastCount = counts[0];
    for (let i = 1; i < colors.length; i += 1) {
      if (counts[i] < leastCount) {
        leastColor = colors[i];
        leastCount = counts[i];
      }
    }
    return leastColor;
  };

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
    if (event.key === ' ') {
      if (inputValue !== '') setInputValue(`${inputValue} `);
      event.preventDefault();
    }
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      const { tagId } = await tagService.addCommunityTag(event.currentTarget.value, calculateColor());
      setInputValue('');
      onTagClick(tagId);
    }
  };

  return (
    <Menu as="div" className="relative">
      <div>
        <Menu.Button className="h-full w-full">{children}</Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95">
        <Menu.Items
          className={twMerge(
            'absolute -top-16 right-14 z-[50] flex max-h-52 w-fit min-w-[200px] flex-col overflow-y-auto rounded-lg bg-secondary-50 p-2 shadow-lg ring-1 ring-black ring-opacity-5',
            className,
          )}>
          <div className="flex flex-wrap items-center gap-1 pb-2 pt-1">
            {tags
              .filter((tag) => communityTagIds.includes(Number(tag.tagId)))
              .map((tag) => (
                <div key={tag.tagId} className="flex gap-1 rounded-full bg-secondary-200 px-2">
                  <p className="text-sm">{tag.name}</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      tagService.removeTagFromCommunity(community.id, tag.tagId);
                      onTagsChanged();
                      e.stopPropagation();
                    }}>
                    <HiX className="h-4 w-4 text-primary-900 hover:opacity-80" />
                  </button>
                </div>
              ))}
          </div>
          <Menu.Item key="add-tag">
            {(): JSX.Element => (
              <input
                type="text"
                value={inputValue}
                onChange={onInputChange}
                onKeyDown={(e): Promise<void> => handleKeyPress(e)}
                onClick={(e): void => e.preventDefault()}
                placeholder={t('page.dashboard.quickAction.addTagToList')}
              />
            )}
          </Menu.Item>
          {visibleTags.map((tag: TagState): JSX.Element | null =>
            !communityTagIds.includes(Number(tag.tagId)) ? (
              <Menu.Item key={tag.tagId}>
                {({ active }): JSX.Element => (
                  <div className="flex h-fit w-full items-center justify-between">
                    <span
                      onClick={(e): void => {
                        onTagClick(tag.tagId);
                        e.stopPropagation();
                      }}
                      className={`${
                        active ? 'bg-secondary-200' : ''
                      } flex w-full cursor-pointer items-center justify-between px-4 py-2 text-sm text-primary-900`}>
                      <p className="mr-10 font-normal">{tag.name}</p>
                      <button
                        type="button"
                        className={`flex h-full items-center ${active ? 'bg-secondary-200' : 'bg-secondary-50'}`}
                        onClick={(e): void => {
                          handleTagDelete(tag.tagId);
                          e.stopPropagation();
                        }}>
                        <HiTrash className={`h-4 w-4 text-primary-900 ${active ? 'bg-secondary-200' : 'bg-secondary-50'} hover:text-opacity-80`} />
                      </button>
                    </span>
                  </div>
                )}
              </Menu.Item>
            ) : null,
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
